import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import SuperAdminLayout from '../SuperAdminLayout';
import { saRequest } from '../../../utils/superAdminAxiosCaller';
import { parseSuperAdminError } from '../../../utils/superAdminErrors';

interface CompanyRow {
    id: number;
    name: string;
    status: 'active' | 'suspended' | 'inactive';
    users_count?: number;
    default_locale: string | null;
    created_at: string | null;
}

interface Paginated<T> {
    data: T[];
    meta?: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function StatusBadge({ status }: { status: CompanyRow['status'] }) {
    return <span className={`sa-badge sa-badge-${status}`}>{status}</span>;
}

// Small inline search icon. Deliberately thin — matches the 1.5px stroke the panel uses elsewhere.
const SearchIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.35-4.35" />
    </svg>
);

const CompaniesListPage: React.FC = () => {
    const history = useHistory();
    const [currentEmail, setCurrentEmail] = useState<string | null>(null);
    const [companies, setCompanies] = useState<CompanyRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        saRequest('GET', 'auth/me')
            .then(res => setCurrentEmail(res.data.super_admin?.email ?? null))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        setPage(1);
    }, [statusFilter, debouncedSearch]);

    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: String(page), per_page: '25' });
            if (statusFilter) params.set('status', statusFilter);
            if (debouncedSearch) params.set('search', debouncedSearch);
            const res = await saRequest<Paginated<CompanyRow>>('GET', `companies?${params.toString()}`);
            setCompanies(res.data.data);
            setTotalPages(res.data.meta?.last_page ?? 1);
            setTotalCount(res.data.meta?.total ?? null);
        } catch (err: any) {
            if (err?.response?.status === 401) {
                history.replace('/mika/ops/login');
                return;
            }
            const p = parseSuperAdminError(err);
            setError(p.banner ?? 'Failed to load companies.');
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter, debouncedSearch, history]);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    const hasFilter = statusFilter !== '' || debouncedSearch !== '';

    return (
        <SuperAdminLayout currentEmail={currentEmail}>
            <div className="sa-page-header">
                <div>
                    <h1>Companies</h1>
                    <div style={{ color: 'var(--sa-text-tertiary)', fontSize: 'var(--sa-fs-sm)', marginTop: 4 }}>
                        {totalCount !== null ? (
                            <>
                                <span className="sa-mono">{totalCount}</span>{' '}
                                {totalCount === 1 ? 'tenant' : 'tenants'}
                                {hasFilter && <> matching current filters</>}
                            </>
                        ) : 'All tenants managed by Mika Ops.'}
                    </div>
                </div>
                <Link
                    to="/mika/ops/companies/new"
                    className="sa-btn sa-btn-primary"
                    style={{ textDecoration: 'none' }}
                >
                    + New company
                </Link>
            </div>

            <div className="sa-card">
                <div className="sa-filters">
                    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flex: '1 1 260px', minWidth: 220, maxWidth: 360 }}>
                        <span
                            aria-hidden
                            style={{
                                position: 'absolute',
                                left: 10,
                                color: 'var(--sa-text-tertiary)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                            }}
                        >
                            <SearchIcon />
                        </span>
                        <input
                            type="search"
                            placeholder="Search by name…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ paddingLeft: 32, width: '100%', minWidth: 0 }}
                        />
                    </div>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                        <option value="">All statuses</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>

                {error && <div className="sa-error-banner">{error}</div>}

                {loading && companies.length === 0 ? (
                    <SkeletonTable />
                ) : companies.length === 0 ? (
                    <div className="sa-empty">
                        <div className="sa-empty-title">
                            {hasFilter ? 'No tenants match' : 'No tenants yet'}
                        </div>
                        <div className="sa-empty-hint">
                            {hasFilter
                                ? 'Try clearing the search or picking a different status.'
                                : 'Create your first tenant to get started — it onboards in under two minutes.'}
                        </div>
                        {!hasFilter && (
                            <Link
                                to="/mika/ops/companies/new"
                                className="sa-btn sa-btn-primary"
                                style={{ textDecoration: 'none' }}
                            >
                                + New company
                            </Link>
                        )}
                    </div>
                ) : (
                    <table className="sa-table">
                        <thead>
                            <tr>
                                <th style={{ width: '40%' }}>Name</th>
                                <th style={{ width: '120px' }}>Status</th>
                                <th style={{ width: '90px', textAlign: 'right' }}>Users</th>
                                <th style={{ width: '100px' }}>Locale</th>
                                <th style={{ width: '140px' }}>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {companies.map(c => (
                                <tr
                                    key={c.id}
                                    className="clickable"
                                    onClick={() => history.push(`/mika/ops/companies/${c.id}`)}
                                >
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ color: 'var(--sa-text)', fontWeight: 500 }}>{c.name}</span>
                                            <span className="sa-mono" style={{ color: 'var(--sa-text-tertiary)', fontSize: 'var(--sa-fs-xs)' }}>
                                                #{c.id}
                                            </span>
                                        </div>
                                    </td>
                                    <td><StatusBadge status={c.status} /></td>
                                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                        {c.users_count ?? '—'}
                                    </td>
                                    <td className="sa-mono" style={{ color: 'var(--sa-text-secondary)' }}>
                                        {c.default_locale ?? '—'}
                                    </td>
                                    <td style={{ color: 'var(--sa-text-tertiary)' }}>{formatDate(c.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {totalPages > 1 && (
                    <div
                        style={{
                            display: 'flex',
                            gap: 'var(--sa-s3)',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: 'var(--sa-s4)',
                        }}
                    >
                        <button
                            type="button"
                            className="sa-btn sa-btn-secondary sa-btn-sm"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                        >
                            ← Previous
                        </button>
                        <span style={{ fontSize: 'var(--sa-fs-sm)', color: 'var(--sa-text-tertiary)' }}>
                            Page <span className="sa-mono" style={{ color: 'var(--sa-text-secondary)' }}>{page}</span> of{' '}
                            <span className="sa-mono" style={{ color: 'var(--sa-text-secondary)' }}>{totalPages}</span>
                        </span>
                        <button
                            type="button"
                            className="sa-btn sa-btn-secondary sa-btn-sm"
                            disabled={page >= totalPages || loading}
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
};

// Five skeleton rows matching the real table's column count.
const SkeletonTable: React.FC = () => (
    <table className="sa-table">
        <thead>
            <tr>
                <th style={{ width: '40%' }}>Name</th>
                <th style={{ width: '120px' }}>Status</th>
                <th style={{ width: '90px', textAlign: 'right' }}>Users</th>
                <th style={{ width: '100px' }}>Locale</th>
                <th style={{ width: '140px' }}>Created</th>
            </tr>
        </thead>
        <tbody>
            {[0, 1, 2, 3, 4].map(i => (
                <tr key={i}>
                    <td colSpan={5} style={{ padding: 0 }}>
                        <div className="sa-skeleton-row" style={{ height: 44 }} />
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

export default CompaniesListPage;
